import React from "react";
import { graphql, useStaticQuery } from "gatsby";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
  pathname?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  keywords?: string;
  jsonLd?: Record<string, any>;
  children?: React.ReactNode;
  noindex?: boolean;
}

export const query = graphql`
  query SEOQuery {
    site {
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        siteUrl
        defaultImage: image
        twitterUsername
        author
      }
    }
  }
`;

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image,
  article = false,
  pathname,
  ogType,
  ogTitle,
  ogDescription,
  ogImage,
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonical,
  keywords,
  jsonLd,
  children,
  noindex = false,
}) => {
  const { site } = useStaticQuery(query);

  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
  } = site.siteMetadata;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image || defaultImage}`,
    url: `${siteUrl}${pathname || ``}`,
    ogType: ogType || (article ? "article" : "website"),
    ogTitle: ogTitle || title || defaultTitle,
    ogDescription: ogDescription || description || defaultDescription,
    ogImage: ogImage
      ? `${siteUrl}${ogImage}`
      : `${siteUrl}${image || defaultImage}`,
    twitterCard,
    twitterTitle: twitterTitle || title || defaultTitle,
    twitterDescription: twitterDescription || description || defaultDescription,
    twitterImage: twitterImage
      ? `${siteUrl}${twitterImage}`
      : `${siteUrl}${image || defaultImage}`,
  };

  return [
    <html lang="en" key="html" />,
    <title key="title">{seo.title}</title>,
    <meta name="description" content={seo.description} key="description" />,
    <meta name="image" content={seo.image} key="image" />,
    ...(noindex ? [<meta name="robots" content="noindex" key="robots" />] : []),

    // OpenGraph tags
    <meta property="og:url" content={seo.url} key="og:url" />,
    <meta property="og:type" content={seo.ogType} key="og:type" />,
    <meta property="og:title" content={seo.ogTitle} key="og:title" />,
    <meta
      property="og:description"
      content={seo.ogDescription}
      key="og:description"
    />,
    <meta property="og:image" content={seo.ogImage} key="og:image" />,

    // Twitter Card tags
    <meta name="twitter:card" content={seo.twitterCard} key="twitter:card" />,
    <meta
      name="twitter:creator"
      content={twitterUsername}
      key="twitter:creator"
    />,
    <meta
      name="twitter:title"
      content={seo.twitterTitle}
      key="twitter:title"
    />,
    <meta
      name="twitter:description"
      content={seo.twitterDescription}
      key="twitter:description"
    />,
    <meta
      name="twitter:image"
      content={seo.twitterImage}
      key="twitter:image"
    />,

    // Additional meta tags
    ...(canonical
      ? [<link rel="canonical" href={canonical} key="canonical" />]
      : []),
    ...(keywords
      ? [<meta name="keywords" content={keywords} key="keywords" />]
      : []),

    // Trustpilot Script
    <script
      type="text/javascript"
      src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
      async
      key="trustpilot"
    />,

    // Poptin Pixel Script
    <script
      id="pixel-script-poptin"
      src="https://cdn.popt.in/pixel.js?id=d439f163745f7"
      async={true}
      key="poptin"
    />,

    // X Pixel
    <script
      dangerouslySetInnerHTML={{
        __html: `
          !function(e,t,n,s,u,a)
          {e.twq ||
            ((s = e.twq =
              function () {
                s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
              }),
            (s.version = "1.1"),
            (s.queue = []),
            (u = t.createElement(n)),
            (u.async = !0),
            (u.src = "https://static.ads-twitter.com/uwt.js"),
            (a = t.getElementsByTagName(n)[0]),
            a.parentNode.insertBefore(u, a))}
          (window,document,'script'); twq('config','p9o1p');
        `,
      }}
      key="x-pixel"
    />,

    children,
  ].filter(Boolean);
};

export default SEO;
